// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { DefaultButton, PrimaryButton, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import { DatePicker, DayOfWeek, IDatePickerStrings } from 'office-ui-fabric-react/lib/DatePicker';
import { ChoiceGroup, IChoiceGroupOption } from 'office-ui-fabric-react/lib/ChoiceGroup';
import dateFormat from 'dateformat';
import moment from 'moment';
import React from 'react';
import ErrorMessage from './../../components/ErrorMessage/ErrorMessage';
import CommonLayout from './../../components/CommonLayout';
import { LoadingState } from './../../store/actionStatus';
import { getWarrantyInfoData, GET_WARRANTY_INFO_DATA } from './../../store/deviceInfo/warranty';
import shieldCheckIcon from './../../assets/ShieldCheck.png';
import Styles from './warrantyUpdate.module.scss';
import DeviceInformation from './../../components/DeviceInformation/DeviceInformation';
import { logPageAction } from './../../lib/ApplicationInsights';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import { useDispatch, useSelector } from 'react-redux';
import { deviceInformationSelector } from './../../store/deviceInfo/selectors';
import { Redirect } from 'react-router';
import { userInfoSelector } from './../../store/user/selector';
import { useTranslation } from 'react-i18next';
import { GetDatePickerTranslations } from './../../helpers/datePickerTranslations';
import auditLogEvents from './../../constants/auditLogEvents';

export interface IState {
    extension: string;
    firstDayOfWeek?: DayOfWeek;
    isWarrantyUpdated: boolean;
    purchaseDate?: Date | null;
    DayPickerStrings?: IDatePickerStrings;
}

const optionStyle = {
    flexContainer: [
        {
            selectors: {
                '.ms-ChoiceField-wrapper': {
                    marginBottom: 20,
                },
                '.ms-ChoiceFieldLabel': {
                    fontSize: 12,
                    lineHeight: 18,
                },
            },
        },
    ],
};

export default function WarrantyUpdate(props: any) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const deviceInformation = useSelector(state =>
        deviceInformationSelector(state)
    );

    const warrantyUpdateStatusState = useSelector(state =>
        statusSelector(state)(GET_WARRANTY_INFO_DATA)
    );
    const errorData = warrantyUpdateStatusState && warrantyUpdateStatusState.error && warrantyUpdateStatusState.error.data;
    const correlationId = errorData && errorData.CorrelationId;
    const errorMessage = errorData && errorData.ErrorMessage;
    const nextServiceAvailableTSString = warrantyUpdateStatusState && warrantyUpdateStatusState.data && warrantyUpdateStatusState.data.NextServiceAvailableTS;
    const nextServiceAvailableTSDate = nextServiceAvailableTSString && moment.utc(nextServiceAvailableTSString).toDate();
    const nextServiceAvailableTSValue = nextServiceAvailableTSDate ? nextServiceAvailableTSDate : 'NA';

    const user = useSelector(state =>
        userInfoSelector(state)
    );

    if (!deviceInformation) {
        return <Redirect to="/" />;
    }

    const [values, setValues] = React.useState<IState>({
        extension: '',
        firstDayOfWeek: DayOfWeek.Sunday,
        isWarrantyUpdated: false,
        purchaseDate: null,
        DayPickerStrings: GetDatePickerTranslations(),
    });
    const { isWarrantyUpdated, firstDayOfWeek, purchaseDate, DayPickerStrings } = values;

    const warrantyUpdateStatus = warrantyUpdateStatusState && warrantyUpdateStatusState.status;
    const { disabled, checked, location } = props;

    const isSpinnerOn = warrantyUpdateStatus === LoadingState.started;
    const checkoutOrderIsError = warrantyUpdateStatus === LoadingState.rejected;
    const deviceDetailData = location.state;
    const checkoutOrderErrorMessage = checkoutOrderIsError ? t('warranty_update_error', { correlationId, errorMessage }) : '';
    const findTechSupportPlanDetail = deviceDetailData.warranties.find((warranty: any) => warranty.entitlementId === 'TechnicalSupportPlan');
    const findStandardPlanDetail = deviceDetailData.warranties.find((warranty: any) => warranty.entitlementId === 'ManufacturerWarranty');

    const onSubmit = (): void => {
        logPageAction('Submit button clicked');

        const { extension, purchaseDate } = values;
        const { serialNumber } = props.location.state;
        if (extension === 'proofOfPurchase') {
            const body = {
                serialNumber,
                AgentEmail: user.email,
                ImageMimeType: null,
                Notes: 'From Variance Tool',
                OrderId: null,
                PurchaseDate: purchaseDate,
                ReceiptImage: null,
                StoreId: null,
            };

            setWarrantyUpdate();
            dispatch(getWarrantyInfoData({ body, extension }));
        } else {
            const body = {
                AgentEmail: user.email,
                Manufacturer: 'Microsoft',
                Reason: 'Support call',
                SerialNumber: serialNumber,
                UserGroup: 'Support T3',
            };

            setWarrantyUpdate();
            dispatch(getWarrantyInfoData({ body, extension }));
        }

        logPageAction(auditLogEvents.WarrantyUpdateEvent, { serialNumber, userId : user.retailUserId});
    };

    const goBack = () => {
        logPageAction('Cancel button clicked');
        props.history.goBack();
    };

    const findDateDifference = (endDate: Date) => {
        const today: any = new Date();
        const end: any = new Date(endDate);
        const diffDays = (end - today) / (1000 * 60 * 60 * 24);
        if (diffDays <= 0) {
            return 0;
        }
        return diffDays.toFixed(0);
    };

    const setWarrantyUpdate = (): void => {
        setValues({ ...values, isWarrantyUpdated: !values.isWarrantyUpdated });
    };

    const onChange = (ev: any, option?: IChoiceGroupOption) => {
        const selectedOption = option && option.key || '';
        logPageAction('Radio button selected', { itemSelected: ev.currentTarget.value });

        setValues({ ...values, extension: selectedOption, purchaseDate: selectedOption === 'proofOfPurchase' ? values.purchaseDate : undefined });
    };

    const onSelectDate = (date: Date | null | undefined): void => {
        setValues({ ...values, purchaseDate: date });
    };

    const onFormatDate = (date: any): any => {
        return `${(date.getMonth() + 1)}/${date.getDate()}/${(date.getFullYear() % 100)}`;
    };

    const onParseDateFromString = (value: string): Date => {
        const date = purchaseDate || new Date();
        const values = (value || '').trim().split('/');
        const day = values.length > 0 ? Math.max(1, Math.min(31, parseInt(values[1], 10))) : date.getDate();
        const month = values.length > 1 ? Math.max(1, Math.min(12, parseInt(values[0], 10))) - 1 : date.getMonth();
        let year = values.length > 2 ? parseInt(values[2], 10) : date.getFullYear();
        if (year < 100) {
            year += date.getFullYear() - (date.getFullYear() % 100);
        }
        return new Date(year, month, day);
    };

    return (
        <CommonLayout pageTitle={[t('warranty_update')]}>
            <div className={Styles.bodyContainer}>
                <div className={Styles.sectionContainer}>
                    <DeviceInformation {...deviceDetailData} />
                </div>
                <div className={Styles.sectionContainer}>

                    {!isWarrantyUpdated ? (
                        <div>
                            <div className={Styles.productDetailDescription}>
                                <div className={Styles.detailLeft}>
                                    <div className={Styles.heading}>{t('warranty_update')}</div>
                                    <div className={Styles.detailLeftDesc}>
                                        {
                                            t(
                                                `warranty_device_change_message`,
                                                { expiryDate: dateFormat(findStandardPlanDetail && findStandardPlanDetail.endDate, 'mediumDate') }
                                            )
                                        }
                                    </div>
                                    <div className={Styles.choiceGroup}>
                                        <div className={Styles.radioContainer}>
                                            <div className={Styles.labels}>
                                                <ChoiceGroup
                                                    options={[
                                                        {
                                                            key: 'proofOfPurchase',
                                                            text: t('proof_of_purchase'),
                                                        },
                                                    ]}
                                                    onChange={onChange}
                                                    selectedKey={values.extension}
                                                    styles={{
                                                        flexContainer: [
                                                            {
                                                                selectors: {
                                                                    '.ms-ChoiceFieldLabel': {
                                                                        fontSize: 12,
                                                                        lineHeight: 18,
                                                                    },
                                                                },
                                                            },
                                                        ],
                                                    }}
                                                />
                                                {t('enter_purchase_date')}
                                                <DatePicker
                                                    ariaLabel={t('enter_purchase_date')}
                                                    disabled={values.extension !== 'proofOfPurchase'}
                                                    isRequired={false}
                                                    allowTextInput={true}
                                                    firstDayOfWeek={firstDayOfWeek}
                                                    strings={DayPickerStrings}
                                                    value={purchaseDate!}
                                                    onSelectDate={onSelectDate}
                                                    formatDate={onFormatDate}
                                                    parseDateFromString={onParseDateFromString}
                                                    placeholder="mm/dd/yyyy"
                                                />
                                            </div>
                                        </div>
                                        <ChoiceGroup
                                            options={[
                                                {
                                                    key: '30',
                                                    ariaLabel: t('30_day_extension'),
                                                    text: t('30_day_extension'),
                                                },
                                            ]}
                                            styles={optionStyle}
                                            onChange={onChange}
                                            selectedKey={values.extension}
                                        />
                                        <ChoiceGroup
                                            options={[
                                                {
                                                    key: '90',
                                                    ariaLabel: t('90_day_extension'),
                                                    text: t('90_day_extension'),
                                                },
                                            ]}
                                            styles={optionStyle}
                                            onChange={onChange}
                                            selectedKey={values.extension}
                                        />
                                    </div>
                                </div>
                                <div className={Styles.detailRight}>
                                    <img src={shieldCheckIcon} alt={t('support_plan')} />
                                    <div className={Styles.detailRightPlan}>
                                        <div className={Styles.headingPlan}>
                                            {t('technical_support_plan')}
                                        </div>
                                        <div className={Styles.headingPlanDetail}>
                                            {t('start_date')}: <span>{dateFormat(findTechSupportPlanDetail && findTechSupportPlanDetail.startDate, 'mediumDate')}</span>
                                        </div>
                                        <div className={Styles.headingPlanDetail}>
                                            {t('end_date')}: <span>{dateFormat(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate, 'mediumDate')}</span>
                                        </div>
                                        {findDateDifference(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate) !== 0 ?
                                            <div className={Styles.headingPlanDetail}>
                                                {t('duration')}:
                                                    <span>
                                                    {`${findDateDifference(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate)} `}
                                                    {t('days_left')}
                                                </span>
                                            </div> :
                                            <div className={Styles.headingPlanDetail}>
                                                <div className={Styles.expired}>{t('warranty_expired')}</div>
                                            </div>}
                                    </div>
                                </div>
                            </div>
                            <div className={Styles.buttons}>
                                <PrimaryButton
                                    data-automation-id="submit"
                                    allowDisabledFocus={true}
                                    checked={checked}
                                    text={t('submit')}
                                    onClick={onSubmit}
                                    className={values.extension ? Styles.submit : Styles.submitDisabled}
                                />
                                <DefaultButton
                                    data-automation-id="cancel"
                                    allowDisabledFocus={true}
                                    disabled={disabled}
                                    checked={checked}
                                    text={t('cancel')}
                                    onClick={goBack}
                                    className={Styles.cancel}
                                />
                            </div>
                        </div>
                    ) : (
                            <div>
                                <div className={Styles.productDetailDescription}>
                                    {warrantyUpdateStatus === LoadingState.success &&
                                        (
                                            <div className={Styles.detailLeft}>
                                                <div className={Styles.headingUpdated}>
                                                    {t('warranty_updated_successfully')}
                                                </div>
                                                <div className={Styles.detailLeftDesc}>
                                                    {
                                                        t
                                                            (`warranty_update_success`,
                                                                { extension: values.extension }
                                                            )
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    {warrantyUpdateStatus === LoadingState.accepted &&
                                        (
                                            <div className={Styles.detailLeft}>
                                                <div className={Styles.headingUpdated}>
                                                    {t('warranty_update_accepted_header')}
                                                </div>
                                            <div className={Styles.detailLeftDesc} style={{ width: '640px', paddingBottom: '14px' }}>
                                                    {t(`warranty_update_accepted_msg`, { serialNumber: deviceDetailData.serialNumber, nextServiceAvailableTS: nextServiceAvailableTSValue })}
                                                </div>
                                            </div>
                                        )}
                                    {warrantyUpdateStatus === LoadingState.rejected &&
                                        (
                                            <div className={Styles.errorContainer}>
                                                <ErrorMessage
                                                    message={checkoutOrderErrorMessage}
                                                />
                                            </div>
                                        )}
                                    {
                                        isSpinnerOn && <Spinner
                                            className={Styles.spinnerStyle}
                                            size={SpinnerSize.medium}
                                        />
                                    }
                                    <div className={Styles.detailRight}>
                                        <img src={shieldCheckIcon} alt={t('support_plan')} />
                                        <div className={Styles.detailRightPlan}>
                                            <div className={Styles.headingPlan}>
                                                Technical Support Plan
                                                </div>
                                            <div className={Styles.headingPlanDetail}>
                                                Start Date: <span>{dateFormat(findTechSupportPlanDetail && findTechSupportPlanDetail.startDate, 'mediumDate')}</span>
                                            </div>
                                            <div className={Styles.headingPlanDetail}>
                                                End Date: <span>{dateFormat(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate, 'mediumDate')}</span>
                                            </div>
                                            {findDateDifference(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate) !== 0 ?
                                                <div className={Styles.headingPlanDetail}>
                                                    {t('duration')}:
                                                    <span>
                                                        {`${findDateDifference(findTechSupportPlanDetail && findTechSupportPlanDetail.endDate)} `}
                                                        {t('days_left')}
                                                    </span>
                                                </div> :
                                                <div className={Styles.headingPlanDetail}>
                                                    <div className={Styles.expired}>Warranty Expired</div>
                                                </div>}

                                        </div>
                                    </div>
                                </div>
                                <div className={Styles.buttons}>
                                    <PrimaryButton
                                        data-automation-id="back"
                                        allowDisabledFocus={true}
                                        disabled={disabled}
                                        checked={checked}
                                        text={t('back_to_device_details')}
                                        onClick={goBack}
                                        className={Styles.backToDeviceDetail}
                                    />
                                </div>
                            </div>
                        )}
                </div>
            </div>
        </CommonLayout>
    );
}
